import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import type { Delivery } from "../../api/ankhInvoices";

type ChipColor = "success" | "warning" | "error" | "default" | "info";

/** An instant as date and time in India. */
function istDateTime(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/**
 * How a document's email is shown in a list (owner 2026-10-15: "no way to see
 * it was delivered"). The words say what the outbox knows and no more: Sent is
 * "accepted by the mail provider", never "read" or "in the inbox".
 */
export function deliveryLabel(d: Delivery | undefined): { label: string; color: ChipColor; tip: string } {
  if (!d) return { label: "—", color: "default", tip: "No email is recorded for this document." };
  const when = istDateTime(d.updatedat);
  switch (d.status) {
    case "Sent":
      return {
        label: "Emailed",
        color: "success",
        tip: `Accepted by the mail provider${d.sentto ? ` for ${d.sentto}` : ""}${when ? ` on ${when} IST` : ""}.`,
      };
    case "Pending":
    case "Claimed":
      return { label: "Queued", color: "info", tip: "Waiting to be emailed; this normally takes a few minutes." };
    case "Failed":
      return {
        label: `Retrying (${d.attempts})`,
        color: "warning",
        tip: `Attempt ${d.attempts} failed${d.detail ? `: ${d.detail}` : ""}. It will be tried again.`,
      };
    case "Abandoned":
      return {
        label: "Not delivered",
        color: "error",
        tip: `Gave up after ${d.attempts} attempts${d.detail ? `: ${d.detail}` : ""}. Send the PDF by hand.`,
      };
    default:
      return { label: d.status || "unknown", color: "default", tip: d.detail ?? "" };
  }
}

export function DeliveryChip({ delivery }: { delivery: Delivery | undefined }) {
  const { label, color, tip } = deliveryLabel(delivery);
  return (
    <Tooltip title={tip}>
      <Chip size="small" label={label} color={color} variant={color === "success" ? "filled" : "outlined"} />
    </Tooltip>
  );
}
